import * as React from 'react';
import {
  DayPicker,
  getDefaultClassNames,
  type DayButton,
  type Locale,
} from 'react-day-picker';

import { cn } from 'cn';
import { Button, buttonVariants } from '@/components/ui/button';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
} from 'lucide-react';
import { useCalendarDrilldown } from './shadcn/custom/DateTimePicker/calendar-drilldown';

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = 'label',
  buttonVariant = 'ghost',
  locale,
  formatters,
  components,
  navigationVariant = 'standard',
  month,
  onMonthChange,
  defaultMonth,
  ...props
}: React.ComponentProps<typeof DayPicker> & {
  buttonVariant?: React.ComponentProps<typeof Button>['variant'];
  navigationVariant?: 'standard' | 'drilldown';
}) {
  const defaultClassNames = getDefaultClassNames();
  const selectedDate =
    navigationVariant === 'drilldown' &&
    props.mode === 'single' &&
    props.selected instanceof Date
      ? props.selected
      : undefined;
  const {
    isDrilldown,
    displayMonth,
    handleMonthChange,
    components: drilldownComponents,
  } = useCalendarDrilldown({
    navigationVariant,
    month,
    defaultMonth,
    onMonthChange,
    selectedDate,
    locale,
  });

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn(
        'tw:group/calendar tw:rounded-xl tw:border tw:border-border tw:bg-background tw:p-3 tw:text-foreground tw:shadow-[0_1px_3px_rgb(0_0_0/0.10)] tw:[--cell-size:--spacing(8)]',
        String.raw`rtl:**:[.rdp-button\_next>svg]:rotate-180`,
        String.raw`rtl:**:[.rdp-button\_previous>svg]:rotate-180`,
        className
      )}
      captionLayout={isDrilldown ? 'label' : captionLayout}
      month={isDrilldown ? displayMonth : month}
      defaultMonth={isDrilldown ? undefined : defaultMonth}
      onMonthChange={isDrilldown ? handleMonthChange : onMonthChange}
      locale={locale}
      formatters={{
        formatMonthDropdown: (date) =>
          date.toLocaleString(locale?.code, { month: 'short' }),
        ...formatters,
      }}
      classNames={{
        root: cn('tw:w-fit', defaultClassNames.root),
        months: cn(
          'tw:relative tw:flex tw:flex-col tw:gap-4 tw:md:flex-row',
          defaultClassNames.months
        ),
        month: cn(
          'tw:flex tw:w-full tw:flex-col tw:gap-4',
          defaultClassNames.month
        ),
        nav: cn(
          'tw:pointer-events-none tw:absolute tw:inset-x-0 tw:top-0 tw:flex tw:w-full tw:items-center tw:justify-between tw:gap-1',
          defaultClassNames.nav
        ),
        button_previous: cn(
          buttonVariants({ variant: buttonVariant }),
          'tw:pointer-events-auto tw:h-(--cell-size) tw:w-10 tw:rounded-md tw:px-3 tw:select-none tw:aria-disabled:opacity-50 tw:focus-visible:border-ring! tw:focus-visible:ring-[3px]! tw:focus-visible:ring-ring/50!',
          defaultClassNames.button_previous
        ),
        button_next: cn(
          buttonVariants({ variant: buttonVariant }),
          'tw:pointer-events-auto tw:h-(--cell-size) tw:w-10 tw:rounded-md tw:px-3 tw:select-none tw:aria-disabled:opacity-50 tw:focus-visible:border-ring! tw:focus-visible:ring-[3px]! tw:focus-visible:ring-ring/50!',
          defaultClassNames.button_next
        ),
        month_caption: cn(
          'tw:flex tw:h-(--cell-size) tw:w-full tw:items-center tw:justify-center tw:px-(--cell-size)',
          defaultClassNames.month_caption
        ),
        dropdowns: cn(
          'tw:flex tw:h-(--cell-size) tw:w-full tw:items-center tw:justify-center tw:gap-1.5 tw:text-sm tw:font-medium',
          defaultClassNames.dropdowns
        ),
        dropdown_root: cn(
          'tw:relative tw:flex tw:h-8 tw:items-center tw:gap-1 tw:rounded-lg tw:border tw:border-border tw:bg-background tw:px-2 tw:py-1.5 tw:text-sm tw:font-medium tw:text-foreground tw:shadow-[0_1px_2px_rgb(0_0_0/0.10)] tw:focus-within:border-ring! tw:focus-within:ring-[3px]! tw:focus-within:ring-ring/50!',
          defaultClassNames.dropdown_root
        ),
        dropdown: cn(
          'tw:absolute tw:inset-0 tw:bg-popover tw:opacity-0',
          defaultClassNames.dropdown
        ),
        caption_label: cn(
          'tw:font-medium tw:select-none',
          isDrilldown
            ? 'tw:flex tw:items-center tw:gap-1 tw:rounded-lg tw:border tw:border-border tw:bg-background tw:px-2 tw:py-1 tw:text-sm tw:shadow-[0_1px_2px_rgb(0_0_0/0.10)] tw:transition-colors tw:[&>svg]:size-3.5 tw:[&>svg]:text-muted-foreground tw:enabled:hover:bg-accent tw:enabled:hover:text-accent-foreground tw:outline-none tw:focus-visible:border-ring! tw:focus-visible:ring-[3px]! tw:focus-visible:ring-ring/50!'
            : captionLayout === 'label'
              ? 'tw:text-sm'
              : 'tw:flex tw:items-center tw:gap-1 tw:rounded-md tw:text-sm tw:[&>svg]:size-3.5 tw:[&>svg]:text-muted-foreground',
          defaultClassNames.caption_label
        ),
        month_grid: cn(
          'tw:w-full tw:border-collapse',
          defaultClassNames.month_grid
        ),
        weekdays: cn('tw:flex tw:translate-x-3', defaultClassNames.weekdays),
        weekday: cn(
          'tw:h-[21px] tw:flex-1 tw:rounded-md tw:text-xs tw:leading-4 tw:font-normal tw:text-popover-foreground tw:select-none',
          defaultClassNames.weekday
        ),
        week: cn('tw:mt-2 tw:flex tw:w-full', defaultClassNames.week),
        week_number_header: cn(
          'tw:w-(--cell-size) tw:select-none',
          defaultClassNames.week_number_header
        ),
        week_number: cn(
          'tw:text-[0.8rem] tw:text-muted-foreground tw:select-none',
          defaultClassNames.week_number
        ),
        day: cn(
          'tw:group/day tw:relative tw:aspect-square tw:h-full tw:w-full tw:rounded-md tw:p-0 tw:text-center tw:select-none tw:[&:last-child[data-selected=true]_button]:rounded-r-md',
          props.showWeekNumber
            ? 'tw:[&:nth-child(2)[data-selected=true]_button]:rounded-l-md'
            : 'tw:[&:first-child[data-selected=true]_button]:rounded-l-md',
          defaultClassNames.day
        ),
        range_start: cn(
          'tw:relative tw:isolate tw:z-0 tw:rounded-l-md tw:bg-accent tw:text-accent-foreground tw:after:absolute tw:after:inset-y-0 tw:after:right-0 tw:after:w-4 tw:after:bg-accent',
          defaultClassNames.range_start
        ),
        range_middle: cn(
          'tw:rounded-none tw:bg-accent tw:text-accent-foreground',
          defaultClassNames.range_middle
        ),
        range_end: cn(
          'tw:relative tw:isolate tw:z-0 tw:rounded-r-md tw:bg-accent tw:text-accent-foreground tw:after:absolute tw:after:inset-y-0 tw:after:left-0 tw:after:w-4 tw:after:bg-accent',
          defaultClassNames.range_end
        ),
        today: cn(
          'tw:rounded-md tw:bg-accent tw:text-accent-foreground tw:data-[selected=true]:rounded-none',
          defaultClassNames.today
        ),
        outside: cn(
          'tw:text-muted-foreground tw:aria-selected:text-muted-foreground',
          defaultClassNames.outside
        ),
        disabled: cn(
          'tw:text-muted-foreground tw:opacity-50',
          defaultClassNames.disabled
        ),
        hidden: cn('tw:invisible', defaultClassNames.hidden),
        ...classNames,
      }}
      components={{
        Root: ({ className, rootRef, ...props }) => {
          return (
            <div
              data-slot="calendar"
              ref={rootRef}
              className={cn(className)}
              {...props}
            />
          );
        },
        Chevron: ({ className, orientation, ...props }) => {
          if (orientation === 'left') {
            return (
              <ChevronLeftIcon
                className={cn('tw:size-4', className)}
                {...props}
              />
            );
          }

          if (orientation === 'right') {
            return (
              <ChevronRightIcon
                className={cn('tw:size-4', className)}
                {...props}
              />
            );
          }

          return (
            <ChevronDownIcon
              className={cn('tw:size-4', className)}
              {...props}
            />
          );
        },
        DayButton: ({ ...props }) => (
          <CalendarDayButton locale={locale} {...props} />
        ),
        WeekNumber: ({ children, ...props }) => {
          return (
            <td {...props}>
              <div className="tw:flex tw:size-(--cell-size) tw:items-center tw:justify-center tw:text-center">
                {children}
              </div>
            </td>
          );
        },
        ...drilldownComponents,
        ...components,
      }}
      {...props}
    />
  );
}

function CalendarDayButton({
  className,
  day,
  modifiers,
  locale,
  ...props
}: React.ComponentProps<typeof DayButton> & { locale?: Partial<Locale> }) {
  const defaultClassNames = getDefaultClassNames();

  const ref = React.useRef<HTMLButtonElement>(null);
  React.useEffect(() => {
    if (modifiers.focused) ref.current?.focus();
  }, [modifiers.focused]);

  return (
    <Button
      variant="ghost"
      size="icon"
      data-day={day.date.toLocaleDateString(locale?.code)}
      data-selected-single={
        modifiers.selected &&
        !modifiers.range_start &&
        !modifiers.range_end &&
        !modifiers.range_middle
      }
      data-range-start={modifiers.range_start}
      data-range-end={modifiers.range_end}
      data-range-middle={modifiers.range_middle}
      className={cn(
        'tw:relative tw:isolate tw:z-10 tw:flex tw:aspect-square tw:h-full tw:w-full tw:min-w-(--cell-size) tw:flex-col tw:gap-1 tw:border-0 tw:text-sm tw:leading-5 tw:font-normal tw:group-data-[focused=true]/day:relative tw:group-data-[focused=true]/day:z-10 tw:group-data-[focused=true]/day:border-ring tw:group-data-[focused=true]/day:ring-[3px] tw:group-data-[focused=true]/day:ring-ring/50 tw:data-[range-end=true]:rounded-md tw:data-[range-end=true]:rounded-r-md tw:data-[range-end=true]:bg-primary tw:data-[range-end=true]:text-primary-foreground tw:data-[range-middle=true]:rounded-none tw:data-[range-middle=true]:bg-accent tw:data-[range-middle=true]:text-accent-foreground tw:data-[range-start=true]:rounded-md tw:data-[range-start=true]:rounded-l-md tw:data-[range-start=true]:bg-primary tw:data-[range-start=true]:text-primary-foreground tw:data-[selected-single=true]:bg-primary tw:data-[selected-single=true]:text-primary-foreground tw:dark:hover:text-foreground tw:[&>span]:text-xs tw:[&>span]:opacity-70',
        defaultClassNames.day,
        className,
        'tw:rounded-md tw:data-[selected-single=true]:rounded-md tw:data-[range-start=true]:rounded-md tw:data-[range-end=true]:rounded-md'
      )}
      {...props}
    />
  );
}

export { Calendar, CalendarDayButton };
